import axios from 'axios';
import { previewPdf } from '../components/PdfPreview';

const API = process.env.REACT_APP_BACKEND_URL;
export const BUREAUS = [['equifax', 'Equifax'], ['experian', 'Experian'], ['transunion', 'TransUnion']];
export const todayIso = () => new Date().toLocaleDateString('en-CA');
export const acctName = (a) => (typeof a === 'string' ? a : a?.account_name || a?.creditor_name || a?.display_name || a?.name || '');
const key = (a) => `${acctName(a).trim().toLowerCase()}|${String((typeof a === 'object' && a?.account_number) || '').trim()}`;

const save = (blob, name) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const errText = async (e) => {
  if (e.response?.data instanceof Blob) {
    try { return JSON.parse(await e.response.data.text()).detail || e.message; } catch { return e.message; }
  }
  return e.response?.data?.detail || e.message;
};

export const uniqueAccounts = (lists) => {
  const seen = new Map();
  lists.flat().forEach((a) => { if (acctName(a) && !seen.has(key(a))) seen.set(key(a), a); });
  return [...seen.values()];
};

// Accounts on the saved FTC report (every section), shaped like letter accounts.
export const ftcAccounts = (report) => Object.entries(report?.sections || {}).flatMap(([num, s]) => (s?.accounts || []).map((a) => ({
  ...a, account_name: a.display_name || a.creditor_name, category: a.category || s.fraudType || '', _section: num,
})));

export const downloadLetter = async (l) => {
  const res = await axios.post(`${API}/api/dispute-letter/generate`, {
    client_name: l.client_name, client_address_lines: l.client_address_lines || [], bureau: l.bureau, bureaus_to_show: l.bureaus_to_show || null, accounts: l.accounts || [],
    letter_type: l.letter_type || 'regular', subject: l.subject || null, pre_body: l.pre_body || null, post_body: l.post_body || null,
    signature_font: l.signature_font || 'cursive_elegant', body_font: l.body_font || 'times', style: l.style || 'typed',
    handwriting_style: l.handwriting_style || null, handwriting_finish: l.handwriting_finish || null, letter_date: l.letter_date || null,
    include_sworn_statement: false,
  }, { responseType: 'blob', timeout: 240000 });
  const m = (res.headers?.['content-disposition'] || '').match(/filename="([^"]+)"/);
  const name = m?.[1] || `${l.letter_type}_${l.bureau}_${l.letter_date}.pdf`;
  const ok = await previewPdf(res.data, name);
  if (ok) save(res.data, name);
  return ok;
};

// Same letter, today's date — saved over the old one, then downloaded.
export const redateLetter = async (l) => {
  const { data } = await axios.patch(`${API}/api/saved-dispute-letters/${l.id}`, { letter_date: todayIso() });
  await downloadLetter(data);
};

// Sworn statement that goes with the letter (same fonts / handwriting style).
export const downloadSworn = async (l) => {
  const res = await axios.post(`${API}/api/dispute-letter/sworn-statement`, {
    client_name: l.client_name, bureau: (l.bureaus_to_show || [l.bureau])[0] === 'all' ? 'experian' : (l.bureaus_to_show || [l.bureau])[0],
    letter_type: l.letter_type, signature_font: l.signature_font || 'cursive_elegant', body_font: l.body_font || 'times',
    style: l.style || 'typed', handwriting_style: l.handwriting_style || null, handwriting_finish: l.handwriting_finish || null,
  }, { responseType: 'blob', timeout: 240000 });
  save(res.data, `Sworn_Statement_${(l.client_name || 'client').replace(/\s+/g, '_')}_${l.letter_type}_${todayIso()}.pdf`);
};

// Round 2 / Final demand: ONE letter with every picked bureau's address stacked at the top,
// replacing this client's earlier letters of the same round. Sworn statement downloads with it.
export const makeRoundLetter = async ({ base, letterType, bureaus, accounts, existing, sworn }) => {
  const old = existing.filter((x) => x.letter_type === letterType);
  await Promise.all(old.map((x) => axios.delete(`${API}/api/saved-dispute-letters/${x.id}`)));
  const { data } = await axios.post(`${API}/api/saved-dispute-letters`, {
    client_id: base.client_id || null, client_name: base.client_name, client_address_lines: base.client_address_lines || [],
    bureau: bureaus.length === 1 ? bureaus[0] : 'all', bureaus_to_show: bureaus.length === 1 ? null : bureaus,
    letter_type: letterType, accounts, signature_font: base.signature_font || 'cursive_elegant', body_font: base.body_font || 'times',
    style: base.style || 'typed', handwriting_style: base.handwriting_style || null, handwriting_finish: base.handwriting_finish || null,
    letter_date: todayIso(),
  });
  if ((await downloadLetter(data)) && sworn) await downloadSworn(data);
};

// Same FTC report minus removed accounts, today's date + time, a brand-new report number; saved over the old one.
export const regenerateFtc = async (report, keepKeys) => {
  const { data: num } = await axios.get(`${API}/api/ftc-affidavit/next-report-number`);
  const reportNumber = num?.next_report_number || null;
  const sections = {};
  const ftcSections = [];
  Object.entries(report.sections || {}).forEach(([n, s]) => {
    const accts = (s?.accounts || []).filter((a) => keepKeys.has(key({ account_name: a.display_name || a.creditor_name, account_number: a.account_number })));
    sections[n] = { ...s, accounts: accts, customTotalAmount: accts.length === (s?.accounts || []).length ? s.customTotalAmount : '' };
    if (!accts.length || !s.fraudType) return;
    const total = sections[n].customTotalAmount || accts.reduce((t, a) => t + Number(a.balance || 0), 0);
    ftcSections.push({
      ftc_section: Number(n), fraud_type: s.fraudType, total_fraud_amount: String(Math.round(Number(total) || 0)),
      accounts: accts.map((a) => ({ creditor_name: a.creditor_name || a.display_name, account_number: a.account_number || '', date_opened: a.date_fraud_began || a.date_opened || '', balance: String(a.balance || 0) })),
    });
  });
  if (!ftcSections.length) throw new Error('No accounts left on the FTC report (or the report has no fraud types set)');
  const ci = report.client_info || {};
  const res = await axios.post(`${API}/api/ftc-affidavit/generate-standalone`, {
    client_info: { name: ci.name || report.client_name || '', suffix: ci.suffix || '', dob: ci.dob || '', ssn: ci.ssn || '', address: ci.address || '',
      city: ci.city || '', state: ci.state || '', zip_code: ci.zip_code || '', phone: ci.phone || '', email: ci.email || report.client_email || '' },
    client_id: report.client_id || null, ftc_sections: ftcSections, custom_statement: report.extra?.personal_statement || null,
    signature_data: report.signature_data || null, custom_report_number: reportNumber, date_format: report.date_format || 'month_year',
    theme: 'color',  // same full-color report as the main FTC workflow
  }, { responseType: 'blob', timeout: 240000 });
  if (reportNumber) axios.post(`${API}/api/ftc-affidavit/save-report-number`, { report_number: reportNumber }).catch(() => {});
  const total = Object.values(sections).reduce((t, s) => t + (s.accounts || []).reduce((x, a) => x + Number(a.balance || 0), 0), 0);
  await axios.put(`${API}/api/ftc-reports/${report.id}`, { sections, report_number: reportNumber, total_fraud_amount: total });
  save(new Blob([res.data], { type: 'application/pdf' }), `FTC_Report_${(report.client_name || 'client').replace(/\s+/g, '_')}_${reportNumber || todayIso()}.pdf`);
  return reportNumber;
};

export const accountKey = key;
